import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import LeiderschapLayout from '@/components/LeiderschapLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { leiderschapApi } from '../api';
import ConfidentialBadge from '../components/ConfidentialBadge';
import MeetingFormDialog from '../components/MeetingFormDialog';
import AgendaTab from '../components/AgendaTab';
import MinutesTab from '../components/MinutesTab';
import DocumentsTab from '../components/DocumentsTab';
import { MEETING_STATUSES, MEETING_TYPES, findClassName, findLabel } from '../labels';
import type { LeadershipMeeting, LeadershipMeetingInput } from '../types';

export default function MeetingDetailPage() {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const { hasCapability } = useAuth();
  const canManage = hasCapability('leiderschap_manage');

  const [meeting, setMeeting] = useState<LeadershipMeeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchMeeting = async () => {
    if (!meetingId) return;
    try {
      const data = await leiderschapApi.getMeeting(meetingId);
      setMeeting(data);
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  const handleUpdate = async (data: LeadershipMeetingInput) => {
    if (!meetingId) return;
    try {
      const updated = await leiderschapApi.updateMeeting(meetingId, data);
      setMeeting(updated);
      toast.success('Vergadering bijgewerkt');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Bijwerken mislukt');
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!meetingId || !meeting) return;
    if (!window.confirm(`Weet je zeker dat je "${meeting.title}" wilt verwijderen?`)) return;
    try {
      await leiderschapApi.deleteMeeting(meetingId);
      toast.success('Vergadering verwijderd');
      navigate('/leiderschap/vergaderingen');
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (!loading && (notFound || !meeting) && meetingId) {
    return (
      <LeiderschapLayout>
        <Card><CardContent className="p-6 text-sm text-slate-500">Vergadering niet gevonden.</CardContent></Card>
      </LeiderschapLayout>
    );
  }

  return (
    <LeiderschapLayout>

      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => navigate('/leiderschap/vergaderingen')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Terug naar vergaderingen
      </Button>

      {meeting && (
        <>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900">{meeting.title}</h2>
                <Badge className={findClassName(MEETING_STATUSES, meeting.status)}>
                  {findLabel(MEETING_STATUSES, meeting.status)}
                </Badge>
                <ConfidentialBadge confidentiality={meeting.confidentiality} />
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {findLabel(MEETING_TYPES, meeting.type)} &middot;{' '}
                {format(parseISO(meeting.scheduled_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
                {meeting.location_or_link ? ` · ${meeting.location_or_link}` : ''}
              </p>
            </div>
            {canManage && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Bewerken
                </Button>
                <Button variant="ghost" size="icon" onClick={handleDelete} aria-label="Verwijderen">
                  <Trash2 className="h-4 w-4 text-slate-400" />
                </Button>
              </div>
            )}
          </div>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <Tabs defaultValue="agenda">
                <TabsList>
                  <TabsTrigger value="agenda">Agenda</TabsTrigger>
                  <TabsTrigger value="notulen">Notulen</TabsTrigger>
                  <TabsTrigger value="documenten">Documenten</TabsTrigger>
                </TabsList>
                <TabsContent value="agenda" className="pt-4">
                  <AgendaTab meetingId={meeting.id} canManage={canManage} />
                </TabsContent>
                <TabsContent value="notulen" className="pt-4">
                  <MinutesTab meetingId={meeting.id} canManage={canManage} />
                </TabsContent>
                <TabsContent value="documenten" className="pt-4">
                  <DocumentsTab meetingId={meeting.id} canManage={canManage} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <MeetingFormDialog open={dialogOpen} onOpenChange={setDialogOpen} meeting={meeting} onSubmit={handleUpdate} />
        </>
      )}
    </LeiderschapLayout>
  );
}
